<template>
  <div>
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-2xl font-bold text-slate-800">Gestión de Usuarios</h2>
      <button @click="showModal = true" class="btn-primary">
        Nuevo Usuario
      </button>
    </div>

    <!-- Lista de usuarios -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div
        v-for="user in users"
        :key="user.uid"
        class="bg-white rounded-lg shadow-sm border border-slate-200 p-6"
      >
        <div class="flex justify-between items-start mb-4">
          <div>
            <h3 class="text-lg font-semibold text-slate-800">{{ user.email }}</h3>
            <p class="text-sm text-slate-600">
              Creado: {{ formatDate(user.createdAt) }}
            </p>
          </div>
          <div class="flex space-x-2">
            <button
              @click="handleEdit(user)"
              class="text-indigo-600 hover:text-indigo-800"
              :disabled="processing"
            >
              Editar
            </button>
            <button
              @click="handleDelete(user)"
              class="text-red-600 hover:text-red-800"
              :disabled="processing || user.uid === currentUser?.uid"
            >
              Eliminar
            </button>
          </div>
        </div>
        
        <div class="space-y-2">
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-slate-700">Rol:</span>
            <span class="text-sm text-slate-600">{{ user.role || 'Usuario' }}</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-slate-700">Último acceso:</span>
            <span class="text-sm text-slate-600">
              {{ formatDate(user.lastSignIn) || 'Nunca' }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de usuario -->
    <Modal
      :show="showModal"
      :title="editingUser ? 'Editar Usuario' : 'Nuevo Usuario'"
      @close="closeModal"
    >
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-2">
            Correo electrónico
          </label>
          <input
            v-model="formData.email"
            type="email"
            class="input-field"
            required
            :disabled="!!editingUser"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-slate-700 mb-2">
            Contraseña
          </label>
          <input
            v-model="formData.password"
            type="password"
            class="input-field"
            :required="!editingUser"
            :placeholder="editingUser ? 'Dejar en blanco para mantener' : ''"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-slate-700 mb-2">
            Rol
          </label>
          <select v-model="formData.role" class="input-field">
            <option value="admin">Administrador</option>
            <option value="user">Usuario</option>
          </select>
        </div>

        <div class="flex justify-end space-x-4">
          <button
            type="button"
            @click="closeModal"
            class="px-4 py-2 text-slate-600 hover:text-slate-800"
            :disabled="processing"
          >
            Cancelar
          </button>
          <button
            type="submit"
            class="btn-primary"
            :disabled="processing"
          >
            {{ processing ? 'Guardando...' : (editingUser ? 'Guardar Cambios' : 'Crear Usuario') }}
          </button>
        </div>
      </form>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useUserStore } from '../../stores/userStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { useAuthStore } from '../../stores/authStore';
import Modal from '../Modal.vue';

const userStore = useUserStore();
const notificationStore = useNotificationStore();
const authStore = useAuthStore();

const showModal = ref(false);
const processing = ref(false);
const editingUser = ref<any>(null);
const users = ref<any[]>([]);
const currentUser = ref(authStore.user);

const formData = ref({
  email: '',
  password: '',
  role: 'user'
});

onMounted(async () => {
  await fetchUsers();
});

const fetchUsers = async () => {
  try {
    users.value = await userStore.getUsers();
  } catch (error) {
    notificationStore.error('Error al cargar usuarios');
  }
};

const formatDate = (date: string | Date | null | undefined) => {
  if (!date) return '';
  return new Date(date).toLocaleString();
};

const handleEdit = (user: any) => {
  editingUser.value = user;
  formData.value = {
    email: user.email,
    password: '',
    role: user.role || 'user'
  };
  showModal.value = true;
};

const handleDelete = async (user: any) => {
  if (user.uid === currentUser.value?.uid) {
    notificationStore.error('No puedes eliminar tu propio usuario');
    return;
  }

  if (!confirm(`¿Está seguro de que desea eliminar el usuario ${user.email}?`)) {
    return;
  }

  processing.value = true;
  try {
    await userStore.deleteUser(user.uid);
    await fetchUsers();
    notificationStore.success('Usuario eliminado correctamente');
  } catch (error) {
    notificationStore.error('Error al eliminar usuario');
  } finally {
    processing.value = false;
  }
};

const closeModal = () => {
  showModal.value = false;
  editingUser.value = null;
  formData.value = {
    email: '',
    password: '',
    role: 'user'
  };
};

const handleSubmit = async () => {
  processing.value = true;
  try {
    if (editingUser.value) {
      await userStore.updateUser(editingUser.value.uid, {
        ...formData.value,
        email: editingUser.value.email
      });
      closeModal();
      notificationStore.success('Usuario actualizado correctamente');
    } else {
      await userStore.createUser(formData.value);
      closeModal();
      notificationStore.success('Usuario creado correctamente');
    }
    await fetchUsers();
  } catch (error) {
    notificationStore.error('Error al guardar usuario');
  } finally {
    processing.value = false;
  }
};
</script>